import React, { useCallback, useEffect, useState } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import AppLayout from '../components/Succeedo/Layout/AppLayout';
import GeorgeConversation from '../components/Succeedo/Conversation/GeorgeConversation';
import useCoachChannel from '../components/Succeedo/Conversation/useCoachChannel';
import { COPY, ARTIFACT_NAMES, DEFAULT_COACH_NAME } from '../components/Succeedo/Conversation/copy';
import SuggestionsSummary from '../components/Succeedo/Conversation/SuggestionsSummary';
import ActionsPanel from '../components/Succeedo/Conversation/ActionsPanel';
import { UPDATE_ITEM } from './SucceedoActionPlanContainer';

// Shared with SucceedoWorkspaceContainer (the artifact library).
export const ARTIFACT_FIELDS = gql`
  fragment CoachArtifactFields on Artifact {
    id
    kind
    version
    title
    headline_value_cents
    summary
    data
    completeness
    change
    latest
    updated_at
  }
`;

export const COACHING_CONVERSATION = gql`
  query CoachingConversation {
    coaching_conversation {
      id
      session_id
      coach_name
      coach_role
      brand_name
      awaiting_reply
      last_error
      suggested_actions
      messages {
        id
        role
        kind
        content
        owner_note
        created_at
        proposed_actions
        valuation_scenarios
        partner_recommendations
        confirmations
        suggested_replies
        rating
        artifacts {
          ...CoachArtifactFields
        }
      }
    }
  }
  ${ARTIFACT_FIELDS}
`;

export const START_CONVERSATION = gql`
  mutation CoachingStartConversation($nudge: String) {
    coaching_start_conversation(nudge: $nudge) {
      conversation {
        id
      }
      errors
    }
  }
`;

const SEND_MESSAGE = gql`
  mutation CoachingSendMessage($content: String!) {
    coaching_send_message(content: $content) {
      message {
        id
      }
      errors
    }
  }
`;

const RETRY_TURN = gql`
  mutation CoachingRetryTurn {
    coaching_retry_turn {
      retrying
      errors
    }
  }
`;

const RATE_MESSAGE = gql`
  mutation CoachingRateMessage($messageId: ID!, $rating: String!) {
    coaching_rate_message(message_id: $messageId, rating: $rating) {
      message {
        id
        rating
      }
      errors
    }
  }
`;

const ACCEPT_ACTION = gql`
  mutation CoachingAcceptAction($proposalKey: String!) {
    coaching_accept_action(proposal_key: $proposalKey) {
      action_item_id
      errors
    }
  }
`;

const DISMISS_ACTION = gql`
  mutation CoachingDismissAction($proposalKey: String!) {
    coaching_dismiss_action(proposal_key: $proposalKey) {
      dismissed
      errors
    }
  }
`;

const SET_ACTIVE_VALUATION = gql`
  mutation CoachingSetActiveValuation($id: ID!) {
    coaching_set_active_valuation(valuation_snapshot_id: $id) {
      active
      errors
    }
  }
`;

const PARTNER_NOT_NOW = gql`
  mutation CoachingPartnerNotNow($needKey: String!) {
    coaching_partner_not_now(need_key: $needKey) {
      not_now
      errors
    }
  }
`;

const CONFIRM_TOOL_CALL = gql`
  mutation CoachingConfirmToolCall($id: ID!, $artifactIds: [ID!]!, $brief: String) {
    coaching_confirm_tool_call(tool_call_id: $id, share_artifact_ids: $artifactIds, brief: $brief) {
      status
      errors
    }
  }
`;

const DECLINE_TOOL_CALL = gql`
  mutation CoachingDeclineToolCall($id: ID!) {
    coaching_decline_tool_call(tool_call_id: $id) {
      status
      errors
    }
  }
`;

const ACTIONS_PANEL = gql`
  query CoachActionsPanel {
    currentUser {
      id
      action_plan {
        items(source: "succeedo") {
          id
          title
          status
          priority
          partner_category
          metadata
        }
        open_count(source: "succeedo")
        completed_count(source: "succeedo")
      }
    }
    active_sprint {
      id
      startOn
      endOn
    }
  }
`;

// The coach's name for the breadcrumb. Read cache-only: the conversation
// query below (and the sidebar's) fill it, so this adds no request.
const COACH_NAME = gql`
  query CoachName {
    coaching_conversation {
      id
      coach_name
    }
  }
`;

const POLL_MS = 4000;
const HOME_PATH = '/succeedo/home';

function track(event, props = {}) {
  if (window.ahoy) window.ahoy.track(event, props);
}

// The George conversation page (P1-08). Loads (or starts) the owner's
// conversation, sends messages, shows live progress from the channel and
// falls back to polling while a reply is pending.
export function SucceedoGeorgeLoader({ renderSidePanel, renderProposals }) {
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(COACHING_CONVERSATION, {
    fetchPolicy: 'cache-and-network',
  });
  const [startConversation, { called: startCalled }] = useMutation(START_CONVERSATION);
  const [sendMessage] = useMutation(SEND_MESSAGE);
  const [retryTurn] = useMutation(RETRY_TURN);
  const [rateMessage] = useMutation(RATE_MESSAGE);
  const [setActiveValuation] = useMutation(SET_ACTIVE_VALUATION);
  const [partnerNotNow] = useMutation(PARTNER_NOT_NOW);
  const [confirmToolCall] = useMutation(CONFIRM_TOOL_CALL);
  const [declineToolCall] = useMutation(DECLINE_TOOL_CALL);

  const [started, setStarted] = useState(false);
  const [localPending, setLocalPending] = useState(false);
  const [progressRows, setProgressRows] = useState([]);
  // The reply as it streams in (Phase 3): only the latest model round, the
  // one the saved message will show. The saved message replaces it.
  const [streaming, setStreaming] = useState(null);
  const [turnError, setTurnError] = useState(null);
  const [draft, setDraft] = useState(() => {
    // "Ask <coach> about this" from the Workspace links here with ?ask=<kind>.
    if (typeof window === 'undefined') return null;
    const kind = new URLSearchParams(window.location.search).get('ask');
    return kind && ARTIFACT_NAMES[kind] ? `About my ${ARTIFACT_NAMES[kind].toLowerCase()}: ` : null;
  });

  const conversation = data?.coaching_conversation;
  const pending = localPending || !!conversation?.awaiting_reply;
  // Once start has run, no conversation means the coach isn't available to
  // this owner any more (the tenant turned it off): their home is elsewhere.
  const unavailable = started && !!data && !conversation;

  // Opening the coach always goes through start: it creates the conversation
  // the first time, opens with what changed after a few days away (Phase 3),
  // and records a click from a nudge email (?nudge=<token>).
  useEffect(() => {
    if (!loading && data && !startCalled) {
      const nudge = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('nudge');
      startConversation({ variables: nudge ? { nudge } : {} })
        .then(() => refetch())
        .then(() => setStarted(true));
    }
  }, [loading, data, startCalled]);

  useEffect(() => {
    if (unavailable) window.location.assign(HOME_PATH);
  }, [unavailable]);

  useEffect(() => {
    if (pending && !turnError && !unavailable) startPolling(POLL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [pending, turnError, unavailable]);

  useEffect(() => {
    if (conversation && !conversation.awaiting_reply) {
      setLocalPending(false);
      setProgressRows([]);
      setStreaming(null);
    }
    if (conversation?.last_error && conversation?.awaiting_reply) {
      setTurnError(COPY.error(conversation.coach_name));
    }
  }, [conversation]);

  useCoachChannel(conversation?.session_id, {
    onThinking: () => {
      setLocalPending(true);
      setTurnError(null);
    },
    onDelta: ({ round, text }) => {
      setStreaming((current) => (current && current.round === round
        ? { round, text: current.text + text }
        : { round, text }));
    },
    onToolProgress: ({ tool, label, artifact_kind: artifactKind }) => {
      setProgressRows((rows) => [
        ...rows.map((r) => ({ ...r, state: 'done' })),
        { key: `${tool}-${rows.length}`, label, state: 'running', artifactKind },
      ]);
    },
    onMessage: () => refetch(),
    onError: (message) => {
      setTurnError(message || COPY.error(conversation?.coach_name || DEFAULT_COACH_NAME));
      setLocalPending(false);
      setProgressRows([]);
      setStreaming(null);
      refetch();
    },
  });

  const onSend = useCallback(async (text) => {
    setTurnError(null);
    setLocalPending(true);
    try {
      const result = await sendMessage({ variables: { content: text } });
      const errors = result?.data?.coaching_send_message?.errors || [];
      if (errors.length) {
        setTurnError(errors[0]);
        setLocalPending(false);
      }
    } catch (e) {
      setTurnError(COPY.error(conversation?.coach_name || DEFAULT_COACH_NAME));
      setLocalPending(false);
    }
    refetch();
  }, [sendMessage, refetch, conversation]);

  const onRetry = useCallback(async () => {
    setTurnError(null);
    setLocalPending(true);
    await retryTurn();
    refetch();
  }, [retryTurn, refetch]);

  // Server records the rating and the event; the cache update shows it.
  const onRate = useCallback((message, rating) => {
    rateMessage({ variables: { messageId: message.id, rating } });
  }, [rateMessage]);

  // The owner's click is what makes a scenario their valuation; the server
  // tells the coach and tracks it. The refetch shows the app event and
  // flips the card to "Now your valuation".
  const onActivateValuation = useCallback(async (scenario) => {
    const result = await setActiveValuation({ variables: { id: String(scenario.valuation_snapshot_id) } });
    const payload = result?.data?.coaching_set_active_valuation;
    if (!payload || payload.errors.length) return false;
    await refetch();
    return true;
  }, [setActiveValuation, refetch]);

  // "Introduce me" is the owner's answer to the coach, in the chat; the
  // coach then asks for consent before anything is shared.
  const onIntroducePartner = useCallback((recommendation, partnerName) => {
    onSend(COPY.introduceMessage(partnerName || 'them'));
  }, [onSend]);

  // Server remembers it for 30 days, tells the coach and tracks it.
  const onPartnerNotNow = useCallback(async (recommendation) => {
    const result = await partnerNotNow({ variables: { needKey: recommendation.need_key } });
    const payload = result?.data?.coaching_partner_not_now;
    if (!payload || payload.errors.length) return false;
    await refetch();
    return true;
  }, [partnerNotNow, refetch]);

  // The owner's consent: only now does anything reach the partner. The
  // server runs the introduction, tells the coach and tracks it. Returns an
  // error message for the sheet, or null.
  const onConfirmToolCall = useCallback(async (confirmation, artifactIds, brief = null) => {
    const result = await confirmToolCall({
      variables: { id: String(confirmation.id), artifactIds: artifactIds.map(String), brief },
    });
    const payload = result?.data?.coaching_confirm_tool_call;
    if (!payload) return COPY.confirmationError;
    if (payload.errors.length) return payload.errors[0];
    await refetch();
    return null;
  }, [confirmToolCall, refetch]);

  const onDeclineToolCall = useCallback(async (confirmation) => {
    const result = await declineToolCall({ variables: { id: String(confirmation.id) } });
    const payload = result?.data?.coaching_decline_tool_call;
    if (!payload) return COPY.confirmationError;
    if (payload.errors.length) return payload.errors[0];
    await refetch();
    return null;
  }, [declineToolCall, refetch]);

  const onAskAbout = useCallback((artifact) => {
    track('succeedo_george_artifact_ask', { kind: artifact.kind });
    setDraft(`About my ${(ARTIFACT_NAMES[artifact.kind] || artifact.title).toLowerCase()}: `);
  }, []);

  if (error && !conversation) {
    return <div className="p-8 text-sm text-coral-ink">Something went wrong. Please refresh the page.</div>;
  }
  if (!conversation) {
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="flex h-[calc(100vh-56px)]">
      <div className="flex-1 min-w-0 pb-14 lg:pb-0">
        <GeorgeConversation
          conversation={conversation}
          pending={pending}
          progressRows={progressRows}
          streamingText={streaming?.text}
          error={turnError}
          onSend={onSend}
          onRetry={onRetry}
          onAskAbout={onAskAbout}
          onRate={onRate}
          onActivateValuation={onActivateValuation}
          onIntroducePartner={onIntroducePartner}
          onPartnerNotNow={onPartnerNotNow}
          onConfirmToolCall={onConfirmToolCall}
          onDeclineToolCall={onDeclineToolCall}
          renderProposals={renderProposals}
          draft={draft}
          onDraftConsumed={() => setDraft(null)}
        />
      </div>
      {renderSidePanel && renderSidePanel({ conversation, refetchConversation: refetch })}
    </div>
  );
}

// The conversation plus the Actions panel (P1-09): the coach's proposals
// wait in the panel ("Suggested by George", from the conversation's
// suggested_actions) with Accept / Not now, and each reply that made some
// says so in one line that opens the panel. Accepted actions live in the
// panel with their dollar impact and 90-day sprint progress.
export function GeorgeWithActions() {
  const { data: plan, refetch: refetchPlan } = useQuery(ACTIONS_PANEL, { fetchPolicy: 'cache-and-network' });
  const [acceptAction] = useMutation(ACCEPT_ACTION);
  const [dismissAction] = useMutation(DISMISS_ACTION);
  const [updateItem] = useMutation(UPDATE_ITEM);
  const [justAddedIds, setJustAddedIds] = useState([]);
  const [revealKey, setRevealKey] = useState(0);

  const items = plan?.currentUser?.action_plan?.items || [];
  const sprint = plan?.active_sprint;

  const onAccept = useCallback(async (proposal) => {
    const result = await acceptAction({ variables: { proposalKey: proposal.key } });
    const payload = result?.data?.coaching_accept_action;
    if (!payload || payload.errors.length) return false;
    setJustAddedIds((ids) => [...ids, String(payload.action_item_id)]);
    refetchPlan();
    return true;
  }, [acceptAction, refetchPlan]);

  const onDismiss = useCallback(async (proposal) => {
    const result = await dismissAction({ variables: { proposalKey: proposal.key } });
    const payload = result?.data?.coaching_dismiss_action;
    if (!payload || payload.errors.length) return false;
    return true;
  }, [dismissAction]);

  const onAdvance = useCallback(async (item, status) => {
    // succeedo_george_action_completed is tracked server-side by the
    // mutation, so it is only recorded when the update actually succeeds.
    await updateItem({ variables: { id: item.id, status: status.toUpperCase() } });
    refetchPlan();
  }, [updateItem, refetchPlan]);

  // A decision changes what's waiting and adds the owner note to the
  // conversation, so the conversation is fetched again too.
  const decided = (decide, refetchConversation) => async (proposal) => {
    const ok = await decide(proposal);
    if (ok) refetchConversation();
    return ok;
  };

  return (
    <SucceedoGeorgeLoader
      renderProposals={(message, conversation) => (
        <SuggestionsSummary
          proposals={message.proposed_actions}
          openKeys={(conversation.suggested_actions || []).map((p) => p.key)}
          coachName={conversation.coach_name}
          onReview={() => setRevealKey((key) => key + 1)}
        />
      )}
      renderSidePanel={({ conversation, refetchConversation }) => (
        <ActionsPanel
          items={items}
          suggestions={conversation.suggested_actions || []}
          sprint={sprint}
          onAdvance={onAdvance}
          onAccept={decided(onAccept, refetchConversation)}
          onDismiss={decided(onDismiss, refetchConversation)}
          justAddedIds={justAddedIds}
          coachName={conversation.coach_name}
          revealKey={revealKey}
        />
      )}
    />
  );
}

// The sidebar's plan progress and My Action Plan badge, from the same items
// the Actions panel lists so the two always agree. The badge counts the
// actions not done yet.
export function planSummary(items) {
  const total = items.length;
  const completed = items.filter((i) => String(i.status).toLowerCase() === 'completed').length;
  return {
    planProgress: { completed, total, percentage: total ? Math.round((completed / total) * 100) : 0 },
    actionPlanBadge: total - completed,
  };
}

// The page shell: the breadcrumb names the tenant's coach once it is known.
// ACTIONS_PANEL is shared with GeorgeWithActions, so its refetches after an
// accept or a status change update the sidebar too.
export function GeorgePage(props) {
  const { data } = useQuery(COACH_NAME, { fetchPolicy: 'cache-only' });
  const { data: plan } = useQuery(ACTIONS_PANEL, { fetchPolicy: 'cache-and-network' });
  const summary = plan ? planSummary(plan.currentUser?.action_plan?.items || []) : {};
  return (
    <AppLayout breadcrumb={COPY.breadcrumb(data?.coaching_conversation?.coach_name)} compactOnTablet {...summary} {...props}>
      <GeorgeWithActions />
    </AppLayout>
  );
}

export default function SucceedoGeorgeContainer(props) {
  return (
    <ApolloProvider client={client}>
      <GeorgePage {...props} />
    </ApolloProvider>
  );
}
